import { Observer } from 'mobx-react';
import * as React from 'react';
import styled from 'styled-components';

import { RESOLUTIONS } from 'src/constants';
import { Button } from 'src/styledComponents/Button';
import { Divider } from 'src/styledComponents/Divider';
import { Dropdown } from 'src/styledComponents/Dropdown';
import { BodyText, Utility } from 'src/styledComponents/Typography';

import { Show } from 'components/condition';
import { Form, InputWrapper } from 'components/form';
import { Loading } from 'components/loading';
import { Body, Content, Controls, Modal, Title } from 'components/modal';

import { t } from 'utils';

import { CHANGE_DISTANCE_FIELD, CHANGE_DISTANCE_FORM, CREATE_CHANGE_DISTANCE_REQUEST } from '../constants';

import { onCancel, onSubmit } from '../actions';

import { changeDistanceModalStore } from '../stores';

import { eligibleDistanceStatus } from '../selections';

const feeLabel = (price: number | null, currency: string | null): string | null => {
  if (!price) {
    return t.staticAsString('changeDistance.form.free');
  }

  return currency ? `${price} ${currency}` : null;
};

const StyledModal = styled(Modal)`
  align-items: center;

  .body {
    position: relative;
    border-radius: 8px;
  }

  @media (max-width: ${RESOLUTIONS.medium}px) {
    .body {
      min-width: 350px;
      width: 90vw;
    }
  }
`;

const StyledDivider = styled(Divider)`
  margin: 16px 0 24px;
  height: 1px;
`;

const StyledControls = styled(Controls)`
  margin-top: 40px;

  button:last-child {
    margin-left: 32px;
  }
`;

const Description = styled(BodyText)`
  display: block;
  margin-bottom: 24px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
`;

const STATUS_LABELS = {
  closed: 'race.closed',
  soldOut: 'race.sold_out',
} as const;

const FormModal = () => {
  return (
    <TestAnchorContext.Provider value={{ container: 'Change-Distance-Popup-Form' }}>
      <Observer>
        {() => {
          const data = changeDistanceModalStore.value;

          if (!data) {
            return null;
          }

          const { distanceName, price, currency, eligibleDistances } = data;
          const fee = feeLabel(price, currency);

          const options = () =>
            eligibleDistances.map((distance) => {
              const status = eligibleDistanceStatus(distance);

              return {
                key: distance.id,
                value: distance.id,
                label: distance.name,
                disabled: Boolean(status),
                note: status ? t.staticAsString(STATUS_LABELS[status]) : undefined,
              };
            });

          return (
            <StyledModal open onClose={onCancel} className='change-distance-modal'>
              <Body>
                <Title>{t.staticAsString('changeDistance.form.title')}</Title>

                <StyledDivider light />

                <Content>
                  <Loading action={CREATE_CHANGE_DISTANCE_REQUEST} opacity={1} />

                  <Description variant='body2'>{t.staticAsString('changeDistance.form.description')}</Description>

                  <InfoRow>
                    <Utility variant='u3' weight='medium'>
                      {t.staticAsString('changeDistance.form.currentDistance')}
                    </Utility>
                    <Utility variant='u3' weight='bold'>
                      {distanceName}
                    </Utility>
                  </InfoRow>

                  <Show if={Boolean(fee)}>
                    <InfoRow>
                      <Utility variant='u3' weight='medium'>
                        {t.staticAsString('changeDistance.form.fee')}
                      </Utility>
                      <Utility variant='u3' weight='bold'>
                        {fee}
                      </Utility>
                    </InfoRow>
                  </Show>

                  <Form id={CHANGE_DISTANCE_FORM}>
                    <InputWrapper
                      name={CHANGE_DISTANCE_FIELD}
                      Component={Dropdown}
                      settings={{
                        requiredMark: true,
                        label: t.staticAsString('changeDistance.form.newDistance'),
                        placeholder: t.staticAsString('registration.nameOfDistance'),
                        options,
                        readOnly: true,
                      }}
                    />
                  </Form>
                </Content>

                <StyledControls>
                  <Button variant='secondary' size='medium' onClick={onCancel} ATN='cancel'>
                    {t.staticAsString('settings.cancel')}
                  </Button>

                  <Button variant='primary' size='medium' onClick={onSubmit} ATN='submit'>
                    {t.staticAsString('changeDistance.form.next')}
                  </Button>
                </StyledControls>
              </Body>
            </StyledModal>
          );
        }}
      </Observer>
    </TestAnchorContext.Provider>
  );
};

export { FormModal };
